import React, { FC, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Box, BlueButton, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { useButton, useI18n, useLogger } from '~Hooks';
import bLogo from '~Assets/Logos/b_logo_black.svg';

import * as actions from '../../actions';
import store from '../../store';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: 100vh;
  width: 100%;
  position: fixed;
  z-index: 99;
`;

const Content = styled(Box)`
  animation: ${fadeIn} 0.3s ease-in-out 0.1s forwards;
  opacity: 0;
  max-width: 420px;
  min-width: 272px;
  width: 100%;
`;

const BLogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  filter: invert(1);
`;

const RetryButton = styled(BlueButton)`
  max-height: 44px;
`;

const LogoutButton = styled(Button)`
  opacity: 0.5;
  max-height: 36px;
  font-size: 13px;
`;

interface Props {
  refetch: () => Promise<any>;
}

const RETRY_INTERVAL_MS = 7000;

const NetworkError: FC<Props> = ({ refetch }) => {
  const { i18n } = useI18n();
  const logger = useLogger('network-error');
  const [retrying, setRetrying] = React.useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleRetry = async () => {
    logger.info('Retrying user query');
    setRetrying(true);
    try {
      await refetch();
    } catch (err) {
      logger.warn('Retry failed', { reason: (err as any)?.message });
    } finally {
      setRetrying(false);
    }
  };

  // Retry once on mount, then every 7 seconds
  useEffect(() => {
    logger.info('Network error screen shown, starting auto-retry');

    // Retry immediately on mount
    handleRetry();

    intervalRef.current = setInterval(() => {
      handleRetry();
    }, RETRY_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const retryButton = useButton({
    onClick: handleRetry,
    disabled: retrying,
  });

  const logoutButton = useButton({
    onClick: () => {
      logger.info('User logged out from network error screen');
      store.dispatch(actions.setToken(null));
    },
  });

  return (
    <Wrapper display="flex" justifyContent="center" alignItems="center">
      <Content
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="large"
        padding="large">
        <Box display="flex" flexDirection="column" alignItems="center" gap="xs">
          <BLogoImg src={bLogo} alt="Baemingo" />
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap="xs">
          <Heading level={2} color="var(--colorWhite)">
            {i18n('System.NetworkError')}
          </Heading>
          <Paragraph level={3} color="var(--colorBlueGray200)" style={{ textAlign: 'center' }}>
            {i18n('App.InternetError')}
          </Paragraph>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap="xs" fluid>
          <RetryButton {...retryButton} fluid size="small">
            {retrying ? <Loader size="sm" color="var(--colorWhite)" /> : i18n('KDS.Reload')}
          </RetryButton>
        </Box>

        <Box marginTop="large">
          <LogoutButton {...logoutButton} size="small">
            {i18n('General.Logout')}
          </LogoutButton>
        </Box>
      </Content>
    </Wrapper>
  );
};

export default NetworkError;
